import { cn } from '../../utils/cn'

function Skeleton({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div data-slot="skeleton" className={cn('bg-skeleton animate-pulse rounded-md', className)} {...props} />
  )
}

/**
 * Status region for a hand-composed group of `Skeleton`s. A pulsing div says
 * nothing to a screen reader, so any loading branch built out of bare
 * `Skeleton`s needs one of these around it — otherwise it silently loses the
 * announcement the `Spinner` it replaced used to make.
 */
function SkeletonGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return <div role="status" aria-label="Loading" className={className} {...props} />
}

interface SkeletonRowsProps extends React.ComponentProps<'div'> {
  rows?: number
  rowClassName?: string
}

/** Stacked bars standing in for a list whose data is still loading. */
function SkeletonRows({ rows = 5, className, rowClassName, ...props }: SkeletonRowsProps) {
  return (
    <SkeletonGroup className={cn('flex flex-col gap-3', className)} {...props}>
      {Array.from({ length: rows }).map((_, index) => (
        <Skeleton key={index} className={cn('h-10 w-full', rowClassName)} />
      ))}
    </SkeletonGroup>
  )
}

// Cycled so labels read like real copy instead of a uniform grid.
const FIELD_LABEL_WIDTHS = ['w-24', 'w-20', 'w-28', 'w-16']

interface SkeletonFieldsProps extends React.ComponentProps<'div'> {
  fields?: number
}

/** Label/value pairs standing in for a definition list that is still loading. */
function SkeletonFields({ fields = 6, className, ...props }: SkeletonFieldsProps) {
  return (
    <SkeletonGroup className={cn('grid grid-cols-2 gap-x-6 gap-y-4', className)} {...props}>
      {Array.from({ length: fields }).map((_, index) => (
        <div key={index} className="flex flex-col gap-1.5">
          <Skeleton className={cn('h-3', FIELD_LABEL_WIDTHS[index % FIELD_LABEL_WIDTHS.length])} />
          <Skeleton className="h-4 w-full" />
        </div>
      ))}
    </SkeletonGroup>
  )
}

interface SkeletonPageProps extends React.ComponentProps<'div'> {
  /** The product's side rail, for a page that loads inside one. */
  rail?: boolean
}

/** A whole page still loading: its heading and content, beside its rail where it has one. */
function SkeletonPage({ rail = false, className, ...props }: SkeletonPageProps) {
  return (
    <SkeletonGroup className={cn('flex min-h-full w-full', className)} {...props}>
      {/* Read out as the spinner it replaces was: a page's loading state has one word to say. */}
      <span className="sr-only">Loading</span>
      {rail && (
        <div className="border-ink-100 hidden w-48 shrink-0 flex-col gap-3 border-r p-4 md:flex">
          {Array.from({ length: 7 }).map((_, index) => (
            <Skeleton key={index} className="h-5 w-full" />
          ))}
        </div>
      )}
      {/* A page's content column, not the window's width: rows that span a wide screen read as nothing. */}
      <div className="flex w-full max-w-4xl flex-col gap-6 p-6">
        <Skeleton className="h-8 w-56" />
        <div className="flex flex-col gap-3">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="h-10 w-full" />
          ))}
        </div>
      </div>
    </SkeletonGroup>
  )
}

export { Skeleton, SkeletonFields, SkeletonGroup, SkeletonPage, SkeletonRows }
